/**
 * Client-side mirror of /backend/engine pure functions, used so calculators can show live
 * results on every keystroke without a network round-trip. The Python engine
 * (backend/engine/calculations.py) remains the source of truth — it is unit-tested and is what
 * the PDF export (Task 6) uses server-side. Keep these two in numeric agreement; when a new
 * function is mirrored here, mirror its backend unit test too.
 *
 * Convention (same as backend): rates are decimals (0.12 for 12%), monthly rate = annual / 12.
 */

import {
  DEBT_FUND_LTCG_CUTOFF_DATE,
  EQUITY_LTCG_EXEMPTION_PER_FY,
  EQUITY_LTCG_RATE,
  EQUITY_STCG_RATE,
  OTHER_ASSET_LTCG_HOLDING_MONTHS,
  OTHER_ASSET_LTCG_RATE,
  PROPERTY_INDEXATION_OPTION_CUTOFF_DATE,
  PROPERTY_LTCG_WITH_INDEXATION_RATE,
} from "../lib/taxConfig";

export function futureValueLumpsum(principal, annualRate, years) {
  return principal * Math.pow(1 + annualRate, years);
}

/** Contribution at START of each month (annuity-due). */
export function futureValueSIP(monthly, annualRate, months) {
  if (months <= 0) return 0;
  const i = annualRate / 12;
  if (i === 0) return monthly * months;
  return monthly * (((Math.pow(1 + i, months) - 1) / i) * (1 + i));
}

/** SIP whose monthly contribution rises by stepUpPct at the start of every year. */
export function futureValueStepUpSIP(startMonthly, annualRate, years, stepUpPct) {
  let balance = 0;
  let monthly = startMonthly;
  for (let year = 0; year < years; year++) {
    const yearContrib = futureValueSIP(monthly, annualRate, 12);
    balance = balance * (1 + annualRate) + yearContrib;
    monthly = monthly * (1 + stepUpPct);
  }
  return balance;
}

export function inflateValue(presentValue, inflationPct, years) {
  return presentValue * Math.pow(1 + inflationPct, years);
}

// ---------------------------------------------------------------------------
// Future value family (Task 14.1) — annual lumpsum, limited-period SIP, lumpsum+SIP, STP legs.
// ---------------------------------------------------------------------------

/** Invest a fixed amount once per year, at the START of each year (annuity-due), for `years`. */
export function futureValueAnnualLumpsum(annualAmount, annualRate, years) {
  if (years <= 0) return 0;
  if (annualRate === 0) return annualAmount * years;
  return annualAmount * (((Math.pow(1 + annualRate, years) - 1) / annualRate) * (1 + annualRate));
}

/** SIP runs for sipYears, then the accumulated corpus is held (no further contributions) until
 * totalYears. If totalYears < sipYears, the SIP simply stops at totalYears. */
export function futureValueLimitedPeriodSIP(monthly, annualRate, sipYears, totalYears) {
  const activeSipYears = Math.min(sipYears, totalYears);
  const fvAtSipEnd = futureValueSIP(monthly, annualRate, activeSipYears * 12);
  const holdYears = Math.max(totalYears - activeSipYears, 0);
  return fvAtSipEnd * Math.pow(1 + annualRate, holdYears);
}

/** A one-time lumpsum plus a monthly SIP, both compounding at the same rate for `years`. */
export function futureValueLumpsumPlusSIP(lumpsum, monthly, annualRate, years) {
  return futureValueLumpsum(lumpsum, annualRate, years) + futureValueSIP(monthly, annualRate, years * 12);
}

/** FV of a ₹1/month STP leg: each month's unit contribution earns one month of `sourceRate` (as
 * if briefly parked in the source fund before the transfer clears) then compounds at `targetRate`
 * for the rest of the tenure. Linear in the transfer amount, so both sipPlusStpFutureValue and
 * requiredStpForTarget (Task 14.2) build on this one loop instead of duplicating it. */
function stpLegUnitFV(sourceRate, targetRate, months) {
  const si = sourceRate / 12;
  const ti = targetRate / 12;
  let target = 0;
  for (let m = 1; m <= months; m++) {
    target += 1 * (1 + si);
    target *= 1 + ti;
  }
  return target;
}

/**
 * Systematic Transfer Plan: `monthlyTransfer` moves out of a source corpus (earning sourceRate)
 * into a target corpus (earning targetRate) every month. The source keeps compounding on its
 * remaining balance; once exhausted, transfers stop (can't transfer more than what's left).
 * Returns the final balances and a month-by-month schedule for charting.
 */
export function stpFutureValue(sourceCorpus, sourceRate, targetRate, monthlyTransfer, months) {
  const si = sourceRate / 12;
  const ti = targetRate / 12;
  let source = sourceCorpus;
  let target = 0;
  const schedule = [];
  for (let m = 1; m <= months; m++) {
    const transfer = Math.min(monthlyTransfer, Math.max(source, 0));
    source = (source - transfer) * (1 + si);
    target = (target + transfer) * (1 + ti);
    schedule.push({ month: m, source: Math.max(source, 0), target });
  }
  return { finalSource: Math.max(source, 0), finalTarget: target, schedule };
}

/**
 * Combines a direct monthly SIP into the target fund (for sipMonths) with a separate STP leg —
 * each month a fresh `monthlyTransfer` contribution earns one month of sourceRate before it
 * compounds at targetRate for the rest of the STP tenure (stpMonths). See stpLegUnitFV.
 */
export function sipPlusStpFutureValue(monthly, sourceRate, targetRate, sipMonths, monthlyTransfer, stpMonths) {
  const directSipFV = futureValueSIP(monthly, targetRate, sipMonths);
  const stpLegFV = monthlyTransfer * stpLegUnitFV(sourceRate, targetRate, stpMonths);
  return directSipFV + stpLegFV;
}

export function requiredSIPForTarget(targetFV, annualRate, months) {
  if (months <= 0) return 0;
  const i = annualRate / 12;
  if (i === 0) return targetFV / months;
  const factor = ((Math.pow(1 + i, months) - 1) / i) * (1 + i);
  return targetFV / factor;
}

export function requiredLumpsumForTarget(targetFV, annualRate, years) {
  return targetFV / Math.pow(1 + annualRate, years);
}

// ---------------------------------------------------------------------------
// Target-based (reverse) family (Task 14.2). futureValueStepUpSIP, futureValueLimitedPeriodSIP,
// and the STP leg are all LINEAR in their contribution amount (the recursions have no constant
// term — zero contribution gives zero FV), so each can be inverted exactly by computing the FV of
// a ₹1 contribution and dividing the target into it, rather than an iterative/numeric search.
// ---------------------------------------------------------------------------

export function requiredAnnualLumpsumForTarget(targetFV, annualRate, years) {
  if (years <= 0) return 0;
  if (annualRate === 0) return targetFV / years;
  const factor = ((Math.pow(1 + annualRate, years) - 1) / annualRate) * (1 + annualRate);
  return targetFV / factor;
}

export function requiredStepUpSIPForTarget(targetFV, annualRate, years, stepUpPct) {
  const unitFV = futureValueStepUpSIP(1, annualRate, years, stepUpPct);
  return unitFV === 0 ? 0 : targetFV / unitFV;
}

export function requiredLimitedPeriodSIPForTarget(targetFV, annualRate, sipYears, totalYears) {
  const unitFV = futureValueLimitedPeriodSIP(1, annualRate, sipYears, totalYears);
  return unitFV === 0 ? 0 : targetFV / unitFV;
}

/** Inverts the STP leg modelled in stpLegUnitFV — see its comment for the modelling assumption. */
export function requiredStpForTarget(targetFV, sourceRate, targetRate, months) {
  const unitFV = stpLegUnitFV(sourceRate, targetRate, months);
  return unitFV === 0 ? 0 : targetFV / unitFV;
}

/** Month-by-month remaining balance under a SWP, floored at 0 once exhausted. */
export function swpBalanceSchedule(corpus, monthlyWithdrawal, annualRate, months) {
  const i = annualRate / 12;
  const schedule = [];
  let exhausted = false;
  for (let n = 1; n <= months; n++) {
    if (exhausted) {
      schedule.push(0);
      continue;
    }
    let balance;
    if (i === 0) {
      balance = corpus - monthlyWithdrawal * n;
    } else {
      balance = corpus * Math.pow(1 + i, n) - monthlyWithdrawal * ((Math.pow(1 + i, n) - 1) / i);
    }
    if (balance <= 0) {
      schedule.push(0);
      exhausted = true;
    } else {
      schedule.push(balance);
    }
  }
  return schedule;
}

/** Months until a SWP corpus is exhausted, or null if it never runs out (100y cap). */
export function swpEndPeriod(corpus, monthlyWithdrawal, annualRate) {
  const i = annualRate / 12;
  const capMonths = 100 * 12;
  if (i > 0 && monthlyWithdrawal <= corpus * i) return null;
  let balance = corpus;
  for (let month = 1; month <= capMonths; month++) {
    balance = balance * (1 + i) - monthlyWithdrawal;
    if (balance <= 0) return month;
  }
  return null;
}

/** Corpus needed so a SWP lasts exactly `months` months (present value of an annuity). */
export function requiredCorpusForSWP(monthlyWithdrawal, annualRate, months) {
  const i = annualRate / 12;
  if (months <= 0) return 0;
  if (i === 0) return monthlyWithdrawal * months;
  return monthlyWithdrawal * ((1 - Math.pow(1 + i, -months)) / i);
}

/** Years-based alias of requiredCorpusForSWP for configs that ask "how big a corpus for a target
 * monthly withdrawal over N years" (Task 14.2) — same PV-of-an-annuity formula, no new maths. */
export function corpusForTargetMonthlySWP(monthlyWithdrawal, annualRate, years) {
  return requiredCorpusForSWP(monthlyWithdrawal, annualRate, Math.round(years * 12));
}

// ---------------------------------------------------------------------------
// SWP family (Task 15.1).
// ---------------------------------------------------------------------------

/** Level monthly withdrawal (annuity payment) that exhausts `corpus` over exactly `years` —
 * the exact inverse of requiredCorpusForSWP (which is linear in the withdrawal amount). */
export function monthlySWPForLumpsum(corpus, annualRate, years) {
  const months = Math.round(years * 12);
  const unitCorpus = requiredCorpusForSWP(1, annualRate, months);
  return unitCorpus === 0 ? 0 : corpus / unitCorpus;
}

/** Build a corpus via SIP for sipYears at buildRate, then find the level monthly withdrawal that
 * exhausts it over withdrawYears at withdrawRate. */
export function monthlySWPForSIP(monthly, buildRate, sipYears, withdrawRate, withdrawYears) {
  const corpus = futureValueSIP(monthly, buildRate, sipYears * 12);
  const monthlyWithdrawal = monthlySWPForLumpsum(corpus, withdrawRate, withdrawYears);
  return { corpus, monthlyWithdrawal };
}

const SWP_FREQUENCY_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };

/** Corpus depletion when a fixed `withdrawal` is taken every `frequency` (monthly/quarterly/
 * annual) instead of every month; growth still compounds monthly between withdrawals. Capped at
 * 100 years like swpEndPeriod/swpBalanceSchedule. */
export function swpPeriodicWithdrawal(corpus, withdrawal, frequency, annualRate) {
  const i = annualRate / 12;
  const monthsPerWithdrawal = SWP_FREQUENCY_MONTHS[frequency] || 1;
  const capMonths = 100 * 12;
  let balance = corpus;
  const schedule = [];
  let endMonth = null;
  for (let m = 1; m <= capMonths; m++) {
    balance *= 1 + i;
    if (m % monthsPerWithdrawal === 0) {
      balance -= withdrawal;
    }
    if (balance <= 0) {
      schedule.push({ month: m, balance: 0 });
      endMonth = m;
      break;
    }
    schedule.push({ month: m, balance });
  }
  return { schedule, endMonth };
}

/**
 * Fixed-horizon (years) two-asset (equity/debt) portfolio: grows each leg at its own rate,
 * withdraws a fixed monthly amount split proportionally to each leg's current share of the total
 * (so withdrawals don't force an off-target allocation), and rebalances back to the target
 * equityPct/debtPct split every `rebalanceMonths`.
 */
export function swpRebalance(corpus, equityPct, debtPct, equityRate, debtRate, withdrawal, years, rebalanceMonths) {
  let equity = corpus * equityPct;
  let debt = corpus * debtPct;
  const ei = equityRate / 12;
  const di = debtRate / 12;
  const months = Math.round(years * 12);
  const schedule = [];
  for (let m = 1; m <= months; m++) {
    equity *= 1 + ei;
    debt *= 1 + di;
    const total = equity + debt;
    if (total > 0) {
      const equityShare = equity / total;
      equity -= withdrawal * equityShare;
      debt -= withdrawal * (1 - equityShare);
    }
    equity = Math.max(equity, 0);
    debt = Math.max(debt, 0);
    if (rebalanceMonths > 0 && m % rebalanceMonths === 0) {
      const totalAfterWithdrawal = equity + debt;
      equity = totalAfterWithdrawal * equityPct;
      debt = totalAfterWithdrawal * debtPct;
    }
    schedule.push({ month: m, equity, debt, total: equity + debt });
  }
  const last = schedule[schedule.length - 1] || { equity: 0, debt: 0, total: 0 };
  return { schedule, finalEquity: last.equity, finalDebt: last.debt, finalTotal: last.total };
}

/**
 * Savings-backed goal plan — the shared maths behind Child Education / Wedding / Dream Home /
 * Car / Vacation / Custom Goal. Inflate today's cost to the goal date, subtract what current
 * savings will have grown to, and size the monthly SIP for the remaining shortfall. `fundedFraction`
 * (0-1) is the slice covered by a loan (home/car) so you only fund the rest yourself; it's 0 for
 * plain savings goals. Rates are decimals (0.12), same convention as the rest of the engine.
 */
export function goalPlan({ presentCost, inflation, years, currentSavings = 0, annualReturn, fundedFraction = 0 }) {
  const futureCost = inflateValue(presentCost, inflation, years);
  const selfFunded = futureCost * (1 - fundedFraction); // down payment for loan goals; full cost otherwise
  const loanFunded = futureCost - selfFunded;
  const existingFV = futureValueLumpsum(currentSavings, annualReturn, years);
  const shortfall = Math.max(selfFunded - existingFV, 0);
  const months = Math.round(years * 12);
  return {
    futureCost,
    selfFunded,
    loanFunded,
    existingFV,
    shortfall,
    monthlySIP: requiredSIPForTarget(shortfall, annualReturn, months),
    requiredLumpsum: requiredLumpsumForTarget(shortfall, annualReturn, years),
  };
}

/**
 * Comprehensive retirement plan (Mission Financial Azadi model): inflate today's expense to the
 * retirement date, size the corpus that sustains it through retirement at the post-retirement
 * rate, subtract what current retirement savings will have grown to, and size the monthly SIP for
 * the shortfall using the expected return on new investments. Rates are decimals.
 */
export function retirementPlan({
  currentAge,
  retirementAge,
  lifeExpectancy,
  monthlyExpense,
  currentSavings = 0,
  inflation,
  postReturn,
  returnOnExisting,
  expectedReturnNew,
}) {
  const yearsToRetire = Math.max(retirementAge - currentAge, 0);
  const retirementYears = Math.max(lifeExpectancy - retirementAge, 0);
  const expenseAtRetirement = inflateValue(monthlyExpense, inflation, yearsToRetire);
  const corpusNeeded = requiredCorpusForSWP(expenseAtRetirement, postReturn, retirementYears * 12);
  const provisionFV = futureValueLumpsum(currentSavings, returnOnExisting, yearsToRetire);
  const shortfall = Math.max(corpusNeeded - provisionFV, 0);
  return {
    yearsToRetire,
    retirementYears,
    expenseAtRetirement,
    corpusNeeded,
    provisionFV,
    shortfall,
    monthlySIP: requiredSIPForTarget(shortfall, expectedReturnNew, yearsToRetire * 12),
  };
}

// ---------------------------------------------------------------------------
// Wealth & Income Strategy (Task 14.3).
// ---------------------------------------------------------------------------

/** Save a fixed % of monthly income as a SIP; both the income (and so the savings amount) step up
 * by annualStepUpPct each year. A thin wrapper over futureValueStepUpSIP — no new maths. */
export function salaryWealthCreation(monthlyIncome, savingsPct, annualStepUpPct, annualRate, years) {
  const startMonthly = monthlyIncome * savingsPct;
  return futureValueStepUpSIP(startMonthly, annualRate, years, annualStepUpPct);
}

/**
 * Illustrates investing your EMI amount via SIP for the loan's tenure: the SIP's gains (future
 * value minus what you put in) are the pool of money available to offset/recover the interest you
 * paid on a loan of the same EMI and tenure. This is an illustrative comparison, not a computation
 * of the loan's actual interest (which needs the loan's own principal/rate, not just its EMI).
 */
export function recoverEmiThroughSip(emi, loanTenureMonths, annualRate) {
  const futureValue = futureValueSIP(emi, annualRate, loanTenureMonths);
  const totalInvested = emi * loanTenureMonths;
  return { futureValue, totalInvested, gains: futureValue - totalInvested };
}

/** Gross yield ignores running costs; net yield subtracts annualExpenses. Both as decimals. */
export function rentalYield(propertyValue, monthlyRent, annualExpenses) {
  const annualRent = monthlyRent * 12;
  const grossYield = propertyValue > 0 ? annualRent / propertyValue : 0;
  const netYield = propertyValue > 0 ? (annualRent - annualExpenses) / propertyValue : 0;
  return { annualRent, grossYield, netYield };
}

const ASSET_ALLOCATION_DEFAULTS = {
  conservative: { equity: 0.3, debt: 0.6, gold: 0.1 },
  moderate: { equity: 0.5, debt: 0.4, gold: 0.1 },
  aggressive: { equity: 0.7, debt: 0.25, gold: 0.05 },
};

export function assetAllocation(riskProfile) {
  return { ...(ASSET_ALLOCATION_DEFAULTS[riskProfile] || ASSET_ALLOCATION_DEFAULTS.moderate) };
}

/** Simple income-replacement HLV method, floored at 0. */
export function hlvCoverNeeded({
  annualIncome,
  supportYears,
  loans,
  existingInvestments,
  existingCover,
  futureGoalCosts = 0,
}) {
  const need = annualIncome * supportYears + loans + futureGoalCosts;
  const available = existingInvestments + existingCover;
  return Math.max(need - available, 0);
}

// ---------------------------------------------------------------------------
// IRR / XIRR (Newton-Raphson). Pre-existing in the Python engine but never mirrored here until
// Task 15.3 needed insuranceIRR as a building block — closing that parity gap now.
// ---------------------------------------------------------------------------

/** Periodic IRR via Newton-Raphson on evenly spaced cashflows (cashflows[0] is period 0). */
export function irr(cashflows, guess = 0.1, tol = 1e-7, maxIter = 200) {
  let rate = guess;
  for (let iter = 0; iter < maxIter; iter++) {
    let npv = 0;
    let dNpv = 0;
    for (let t = 0; t < cashflows.length; t++) {
      npv += cashflows[t] / Math.pow(1 + rate, t);
      dNpv += (-t * cashflows[t]) / Math.pow(1 + rate, t + 1);
    }
    if (dNpv === 0) break;
    const newRate = rate - npv / dNpv;
    if (Math.abs(newRate - rate) < tol) return newRate;
    rate = newRate;
  }
  return rate;
}

/** XIRR via Newton-Raphson for irregularly-dated cashflows (annualised rate). `dates` are JS
 * Date objects. */
export function xirr(dates, cashflows, guess = 0.1, tol = 1e-7, maxIter = 200) {
  if (dates.length !== cashflows.length) {
    throw new Error("dates and cashflows must be the same length");
  }
  const t0 = dates[0];
  const years = dates.map((d) => (d - t0) / (1000 * 60 * 60 * 24) / 365.0);

  let rate = guess;
  for (let iter = 0; iter < maxIter; iter++) {
    let npv = 0;
    let dNpv = 0;
    for (let i = 0; i < years.length; i++) {
      npv += cashflows[i] / Math.pow(1 + rate, years[i]);
      dNpv += (-years[i] * cashflows[i]) / Math.pow(1 + rate, years[i] + 1);
    }
    if (dNpv === 0) break;
    const newRate = rate - npv / dNpv;
    if (Math.abs(newRate - rate) < tol) return newRate;
    rate = newRate;
  }
  return rate;
}

// ---------------------------------------------------------------------------
// Insurance & Protection (Task 15.3).
// ---------------------------------------------------------------------------

/**
 * IRR of an insurance policy's cashflow stream: premiums[i] (outflow) and payouts[i] (inflow)
 * both occur in year years[i] (0-indexed, same convention as `irr`'s cashflows[0] = year 0).
 * Builds one dense annual net-cashflow array and reuses the periodic `irr`.
 */
export function insuranceIRR(premiums, payouts, years) {
  const maxYear = years.length > 0 ? Math.max(...years) : 0;
  const cashflows = new Array(maxYear + 1).fill(0);
  for (let i = 0; i < years.length; i++) {
    cashflows[years[i]] += (payouts[i] || 0) - (premiums[i] || 0);
  }
  return irr(cashflows);
}

/**
 * Surrendering now: the surrender value plus what you'd otherwise keep paying in premiums, both
 * invested at altRate for the yearsLeft remaining on the policy. Compared against just continuing
 * to maturity. A positive `benefit` means surrendering and investing the difference wins.
 */
export function surrenderBenefit(surrenderValue, remainingAnnualPremium, yearsLeft, maturityValue, altRate) {
  const surrenderAndInvest =
    futureValueLumpsum(surrenderValue, altRate, yearsLeft) +
    futureValueAnnualLumpsum(remainingAnnualPremium, altRate, yearsLeft);
  return { surrenderAndInvest, continuePolicy: maturityValue, benefit: surrenderAndInvest - maturityValue };
}

/**
 * Compares a traditional (endowment-style) policy against buying pure term cover and investing
 * the premium difference. Both are assumed to provide the same cover, so `cover` doesn't enter
 * the maths — it's carried through only for display.
 */
export function insuranceVsTermPlusSIP(traditionalAnnualPremium, cover, termAnnualPremium, sipRate, years, traditionalMaturity) {
  const diffAnnual = Math.max(traditionalAnnualPremium - termAnnualPremium, 0);
  const termPlusSipCorpus = futureValueAnnualLumpsum(diffAnnual, sipRate, years);
  return { termPlusSipCorpus, traditionalMaturity, advantage: termPlusSipCorpus - traditionalMaturity };
}

// ---------------------------------------------------------------------------
// FIRE (Financial Independence, Retire Early) — SWR-based perpetual withdrawal.
// Unlike `retirementPlan` which uses a finite drawdown to life expectancy, the FIRE model
// assumes the corpus lasts forever if you only withdraw at the Safe Withdrawal Rate (SWR).
// Lean FIRE = 3% (essentials-only), Regular FIRE = 4% (classic Trinity study), Fat FIRE = 2.5%
// (comfortable). Higher SWR = smaller corpus needed but higher risk of running out.
// ---------------------------------------------------------------------------

/**
 * The corpus required at retirement so that withdrawing `annualExpensesAtRetirement` at `swrPct`
 * per year sustains indefinitely: corpus × SWR = annual expenses → corpus = expenses / SWR.
 */
export function fireCorpusFromSWR(annualExpensesAtRetirement, swrPct) {
  if (swrPct <= 0) return Infinity;
  return annualExpensesAtRetirement / swrPct;
}

/**
 * Comprehensive Lean/Regular/Fat FIRE plan. Same signature idea as `retirementPlan` but the
 * corpus is derived from SWR instead of a finite SWP schedule. Returns the trio of corpus
 * targets, the SIP needed to hit the primary (leanSwr) target, and the year-by-year projection
 * of the invested corpus so the chart can show milestone crossings.
 */
export function fireLeanPlan({
  currentAge,
  retirementAge,
  monthlyExpense,
  currentSavings = 0,
  inflation,
  preRetirementReturn,
  returnOnExisting,
  swrLeanPct = 0.03,
  swrRegularPct = 0.04,
  swrFatPct = 0.025,
}) {
  const yearsToRetire = Math.max(retirementAge - currentAge, 0);
  const monthlyExpenseAtRetirement = inflateValue(monthlyExpense, inflation, yearsToRetire);
  const annualExpenseAtRetirement = monthlyExpenseAtRetirement * 12;
  const leanCorpus = fireCorpusFromSWR(annualExpenseAtRetirement, swrLeanPct);
  const regularCorpus = fireCorpusFromSWR(annualExpenseAtRetirement, swrRegularPct);
  const fatCorpus = fireCorpusFromSWR(annualExpenseAtRetirement, swrFatPct);
  const provisionFV = futureValueLumpsum(currentSavings, returnOnExisting, yearsToRetire);
  const shortfall = Math.max(leanCorpus - provisionFV, 0);
  const monthlySIP = requiredSIPForTarget(shortfall, preRetirementReturn, yearsToRetire * 12);
  // Year-by-year projection: existing corpus grows at returnOnExisting, plus SIP contributions
  // grow at preRetirementReturn. This is what feeds the chart.
  const projection = [];
  for (let y = 0; y <= yearsToRetire; y++) {
    const existingFV = futureValueLumpsum(currentSavings, returnOnExisting, y);
    const sipFV = futureValueSIP(monthlySIP, preRetirementReturn, y * 12);
    projection.push({
      year: y,
      age: currentAge + y,
      corpus: Math.round(existingFV + sipFV),
    });
  }
  return {
    yearsToRetire,
    monthlyExpenseAtRetirement,
    annualExpenseAtRetirement,
    leanCorpus,
    regularCorpus,
    fatCorpus,
    provisionFV,
    shortfall,
    monthlySIP,
    projection,
  };
}

// ---------------------------------------------------------------------------
// Traditional (LIC / Kotak) endowment / whole-life / money-back analyser.
// The user knows premium + terms; we project maturity from a bonus assumption and derive the
// actual IRR + opportunity cost against an alt-return SIP. Money-back handled as a % of SA paid
// out at the halfway point of the policy term (a common LIC MB structure) OR zero for endowment.
// ---------------------------------------------------------------------------

/**
 * Project the maturity value of a traditional endowment / whole-life policy from LIC's standard
 * bonus + FAB formula:
 *   maturity = sum_assured + (bonus_per_1000 × SA/1000 × policy_term_years) + (fab_per_1000 × SA/1000)
 * FAB (Final Additional Bonus) is only credited for policies of ≥15 years — set to 0 otherwise
 * automatically at call site.
 */
export function projectPolicyMaturity(sumAssured, policyTermYears, bonusPer1000, fabPer1000) {
  const saUnits = sumAssured / 1000;
  const accumulatedBonus = bonusPer1000 * saUnits * policyTermYears;
  const finalAdditionalBonus = fabPer1000 * saUnits;
  return {
    guaranteed: sumAssured,
    bonus: accumulatedBonus,
    fab: finalAdditionalBonus,
    total: sumAssured + accumulatedBonus + finalAdditionalBonus,
  };
}

/**
 * Full traditional-policy analysis. Composes projectPolicyMaturity + insuranceIRR +
 * insuranceVsTermPlusSIP into one call. `moneyBackPct` (0-100) triggers a single payout equal to
 * moneyBackPct% of SA at the midpoint of the policy term — a common LIC MB simplification. Set
 * to 0 for pure endowment / whole-life.
 */
export function analyseTraditionalPolicy({
  sumAssured,
  annualPremium,
  premiumYears,
  policyTermYears,
  bonusPer1000,
  fabPer1000,
  moneyBackPct = 0,
  altReturn = 0.12,
}) {
  const maturity = projectPolicyMaturity(sumAssured, policyTermYears, bonusPer1000, fabPer1000);
  const totalPremiumsPaid = annualPremium * premiumYears;
  const n = policyTermYears + 1;
  const premiums = new Array(n).fill(0);
  const payouts = new Array(n).fill(0);
  const years = Array.from({ length: n }, (_, i) => i);
  for (let i = 0; i < premiumYears && i < n; i++) premiums[i] = annualPremium;
  // Money-back payout at midpoint (rounded down), only when moneyBackPct > 0.
  const mbYear = Math.floor(policyTermYears / 2);
  const mbAmount = (moneyBackPct / 100) * sumAssured;
  if (mbAmount > 0 && mbYear > 0 && mbYear < policyTermYears) {
    payouts[mbYear] = mbAmount;
  }
  // Final maturity is net of any MB already paid — LIC keeps its promise but doesn't double-pay.
  payouts[policyTermYears] = Math.max(maturity.total - mbAmount, 0);
  const irrRate = insuranceIRR(premiums, payouts, years);
  // Opportunity cost: same annualPremium invested in a `altReturn` annual-lumpsum SIP for the
  // full policy term. Compared against maturity.total (before subtracting MB, because that MB
  // was received back as cash and could also have been invested — parity assumption).
  const altCorpus = futureValueAnnualLumpsum(annualPremium, altReturn, premiumYears);
  // Grow the alt corpus at altReturn for the remaining years after PPT ends, if any.
  const remainingYears = Math.max(policyTermYears - premiumYears, 0);
  const altCorpusAtMaturity = futureValueLumpsum(altCorpus, altReturn, remainingYears);
  return {
    maturity,
    totalPremiumsPaid,
    mbPayout: mbAmount,
    mbYear: mbAmount > 0 ? mbYear : null,
    finalPayout: payouts[policyTermYears],
    irr: irrRate,
    altCorpusAtMaturity,
    opportunityGap: altCorpusAtMaturity - maturity.total,
    cashflowYears: years,
    cashflowPremiums: premiums,
    cashflowPayouts: payouts,
  };
}

// Tax — see frontend/src/lib/taxConfig.js for rates and AS_OF version. Indicative only.
export function capitalGainsTax(
  assetClass,
  purchaseValue,
  saleValue,
  holdingMonths,
  purchaseDate,
  slabRatePct,
  ltcgExemptionAlreadyUsed = 0
) {
  const gain = saleValue - purchaseValue;
  if (gain <= 0) {
    return { gain, isLongTerm: false, tax: 0, netProceeds: saleValue, notes: "" };
  }

  const slabRate = slabRatePct / 100;
  let isLongTerm = false;
  let tax = 0;
  let notes = "";

  if (assetClass === "equity") {
    isLongTerm = holdingMonths >= 12;
    if (isLongTerm) {
      const exemptionRemaining = Math.max(0, EQUITY_LTCG_EXEMPTION_PER_FY - ltcgExemptionAlreadyUsed);
      const taxable = Math.max(0, gain - exemptionRemaining);
      tax = taxable * EQUITY_LTCG_RATE;
    } else {
      tax = gain * EQUITY_STCG_RATE;
    }
  } else if (assetClass === "debt_fund") {
    if (purchaseDate >= DEBT_FUND_LTCG_CUTOFF_DATE) {
      isLongTerm = false;
      tax = gain * slabRate;
      notes = "Bought on/after 1-Apr-2023: taxed at slab rate regardless of holding period.";
    } else {
      isLongTerm = holdingMonths >= 36;
      tax = gain * (isLongTerm ? OTHER_ASSET_LTCG_RATE : slabRate);
    }
  } else if (assetClass === "property") {
    isLongTerm = holdingMonths >= OTHER_ASSET_LTCG_HOLDING_MONTHS;
    if (isLongTerm) {
      if (purchaseDate < PROPERTY_INDEXATION_OPTION_CUTOFF_DATE) {
        const newRegimeTax = gain * OTHER_ASSET_LTCG_RATE;
        const oldRegimeTax = gain * PROPERTY_LTCG_WITH_INDEXATION_RATE;
        tax = Math.min(newRegimeTax, oldRegimeTax);
        notes =
          "Bought before 23-Jul-2024: lower of 12.5% (no indexation) and 20% " +
          "(with indexation — indexation benefit not modelled here, so this is conservative) is used.";
      } else {
        tax = gain * OTHER_ASSET_LTCG_RATE;
      }
    } else {
      tax = gain * slabRate;
    }
  } else {
    isLongTerm = holdingMonths >= OTHER_ASSET_LTCG_HOLDING_MONTHS;
    tax = gain * (isLongTerm ? OTHER_ASSET_LTCG_RATE : slabRate);
  }

  return { gain, isLongTerm, tax, netProceeds: saleValue - tax, notes };
}

export function capitalGainsBondVsInvestment(ltcgAmount, applicableTaxRatePct, bondRate, bondLockYears, altReturnRate) {
  const taxNow = ltcgAmount * (applicableTaxRatePct / 100);
  const payTaxAndInvest = futureValueLumpsum(ltcgAmount - taxNow, altReturnRate, bondLockYears);
  const bondInvestment = futureValueLumpsum(ltcgAmount, bondRate, bondLockYears);
  return { payTaxAndInvest, bondInvestment, advantage: bondInvestment - payTaxAndInvest };
}

export function debtFundHoldSell(currentValue, costBasis, purchaseDate, slabRatePct, expectedAnnualRate, extraHoldYears) {
  const slabRate = slabRatePct / 100;

  const gainNow = Math.max(0, currentValue - costBasis);
  const taxNow = gainNow * slabRate;
  const netNow = currentValue - taxNow;
  const sellNowResult = futureValueLumpsum(netNow, expectedAnnualRate, extraHoldYears);

  const futureValue = futureValueLumpsum(currentValue, expectedAnnualRate, extraHoldYears);
  const gainThen = Math.max(0, futureValue - costBasis);
  const taxThen = gainThen * slabRate;
  const holdMoreResult = futureValue - taxThen;

  return { sellNowResult, holdMoreResult, advantage: holdMoreResult - sellNowResult };
}

function debtPriorityOrder(working, strategy, hybridThreshold) {
  if (strategy === "avalanche") return [...working].sort((a, b) => b.rate - a.rate);
  if (strategy === "snowball") return [...working].sort((a, b) => a.balance - b.balance);
  if (strategy === "hybrid") {
    const quickWins = working
      .filter((d) => d.balance <= hybridThreshold)
      .sort((a, b) => a.balance - b.balance);
    const rest = working
      .filter((d) => d.balance > hybridThreshold)
      .sort((a, b) => b.rate - a.rate);
    return [...quickWins, ...rest];
  }
  throw new Error(`unknown strategy: ${strategy}`);
}

/**
 * Month-by-month debt payoff simulation — mirrors backend/engine debt_payoff_schedule.
 * debts: [{ name, balance, rate (decimal), minPayment }]. strategy: avalanche | snowball | hybrid.
 * Throws if monthlyBudget is below the sum of minimum payments.
 */
export function debtPayoffSchedule(debts, monthlyBudget, strategy = "avalanche", hybridThreshold = 0) {
  const totalMin = debts.reduce((sum, d) => sum + d.minPayment, 0);
  if (monthlyBudget < totalMin) {
    throw new Error("monthlyBudget must be at least the sum of minimum payments");
  }

  const working = debts.map((d) => ({ name: d.name, balance: d.balance, rate: d.rate, min: d.minPayment }));
  let months = 0;
  let totalInterest = 0;
  const payoffOrder = [];
  const maxMonths = 100 * 12;
  const balanceHistory = [working.reduce((sum, d) => sum + d.balance, 0)];

  while (working.some((d) => d.balance > 0.01) && months < maxMonths) {
    months += 1;

    for (const d of working) {
      if (d.balance > 0) {
        const interest = d.balance * (d.rate / 12);
        d.balance += interest;
        totalInterest += interest;
      }
    }

    let leftover = monthlyBudget;
    for (const d of working) {
      if (d.balance > 0) {
        const pay = Math.min(d.min, d.balance);
        d.balance -= pay;
        leftover -= pay;
      }
    }

    for (const d of debtPriorityOrder(working, strategy, hybridThreshold)) {
      if (leftover <= 0) break;
      if (d.balance <= 0) continue;
      const pay = Math.min(leftover, d.balance);
      d.balance -= pay;
      leftover -= pay;
    }

    for (const d of working) {
      if (d.balance <= 0.01 && !payoffOrder.includes(d.name)) {
        d.balance = 0;
        payoffOrder.push(d.name);
      }
    }

    balanceHistory.push(working.reduce((sum, d) => sum + d.balance, 0));
  }

  return {
    strategy,
    monthsToDebtFree: months < maxMonths ? months : null,
    totalInterest,
    payoffOrder,
    balanceHistory,
  };
}
